import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';
import { getBackendUrl } from '../config';
import { buildEnergyMeterDetailPath } from '../utils/energyMeterBrandDisplay';

const BACKEND_URL = getBackendUrl();

const DeviceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [device, setDevice] = useState(null);
  const [reading, setReading] = useState(null);
  const [inverters, setInverters] = useState([]);
  const [energyMeters, setEnergyMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('inverter');
  const [addMethod, setAddMethod] = useState('manual');
  const [formData, setFormData] = useState({ serial_number: '', name: '', model: '', rated_power_kw: '' });
  const [saving, setSaving] = useState(false);

  // Photo upload states
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoTarget, setPhotoTarget] = useState(null); // {type: 'data-logger'|'inverter'|'energy-meter', id: string, name: string}
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Triple click tracking
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const getToken = () => localStorage.getItem('access_token');

  const fetchDevice = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devices/data-loggers/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setDevice(response.data);
    } catch (error) {
      console.error('Error fetching device:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchReading = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/energy-readings/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setReading(response.data);
    } catch (error) {
      console.error('Error fetching reading:', error);
    }
  }, [id]);

  const fetchInverters = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devices/inverters?data_logger_id=${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setInverters(response.data);
    } catch (error) {
      console.error('Error fetching inverters:', error);
    }
  }, [id]);

  const fetchEnergyMeters = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devices/energy-meters?data_logger_id=${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setEnergyMeters(response.data);
    } catch (error) {
      console.error('Error fetching energy meters:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchDevice();
    fetchReading();
    fetchInverters();
    fetchEnergyMeters();
  }, [fetchDevice, fetchReading, fetchInverters, fetchEnergyMeters]);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      toast.error('Vetëm SUPERADMIN mund të shtojë pajisje');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        serial_number: formData.serial_number,
        name: formData.name,
        data_logger_id: id,
        model: formData.model || null,
        rated_power_kw: parseFloat(formData.rated_power_kw) || 100
      };

      if (addType === 'inverter') {
        await axios.post(`${BACKEND_URL}/api/devices/inverters`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        fetchInverters();
      } else {
        await axios.post(`${BACKEND_URL}/api/devices/energy-meters`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        fetchEnergyMeters();
      }

      setShowAddModal(false);
      setFormData({ serial_number: '', name: '', model: '', rated_power_kw: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë shtimit të pajisjes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, deviceId, deviceName) => {
    if (!isSuperAdmin) {
      toast.error('Vetëm SUPERADMIN mund të fshijë pajisje');
      return;
    }

    const ok = await confirm({ title: 'Konfirmo fshirjen', message: `A jeni të sigurt që dëshironi të fshini "${deviceName}"?`, confirmLabel: 'Fshi', cancelLabel: 'Jo' });
    if (!ok) return;

    try {
      const endpoint = type === 'inverter'
        ? `${BACKEND_URL}/api/devices/inverters/${deviceId}`
        : `${BACKEND_URL}/api/devices/energy-meters/${deviceId}`;

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      if (type === 'inverter') {
        fetchInverters();
      } else {
        fetchEnergyMeters();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë fshirjes');
    }
  };

  const openAddModal = (type) => {
    if (!isSuperAdmin) {
      toast.error('Vetëm SUPERADMIN mund të shtojë pajisje');
      return;
    }
    setAddType(type);
    setAddMethod('manual');
    setFormData({ serial_number: '', name: '', model: '', rated_power_kw: '' });
    setShowAddModal(true);
  };

  // Handle triple click on photo to open upload modal
  const handlePhotoClick = (targetType, targetId, targetName, currentPhotoUrl) => {
    if (!isSuperAdmin) return;

    clickCountRef.current += 1;

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    if (clickCountRef.current >= 3) {
      // Triple click detected - open photo upload modal
      clickCountRef.current = 0;
      setPhotoTarget({ type: targetType, id: targetId, name: targetName, currentPhoto: currentPhotoUrl });
      setShowPhotoModal(true);
    } else {
      // Reset after 500ms if not triple clicked
      clickTimerRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, 500);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !photoTarget) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/upload/photo/${photoTarget.type}/${photoTarget.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Refresh data
      if (photoTarget.type === 'data-logger') {
        fetchDevice();
      } else if (photoTarget.type === 'inverter') {
        fetchInverters();
      } else {
        fetchEnergyMeters();
      }

      setShowPhotoModal(false);
      setPhotoTarget(null);
      toast.success('Foto u ngarkua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë ngarkimit të fotos');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Duke ngarkuar...</div>
        </div>
      </Layout>
    );
  }

  if (!device) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-xl text-red-600">Pajisja nuk u gjet</p>
          <button onClick={() => navigate('/devices')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">
            Kthehu te Lista
          </button>
        </div>
      </Layout>
    );
  }

  const tabs = [
    { id: 'all', label: 'All', count: inverters.length + energyMeters.length },
    { id: 'inverters', label: 'Inverters', count: inverters.length },
    { id: 'meters', label: 'E. Meters', count: energyMeters.length },
    { id: 'accessories', label: 'Accessories', count: 0 }
  ];

  const defaultPhoto = "https://customer-assets.emergentagent.com/job_muj-pytja-start/artifacts/8a2lsskt_IMG-20251218-WA0002.jpg";

  return (
    <Layout>
      <div className="p-3 md:p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => navigate('/devices')} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">{device.name}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">SN: {device.serial_number}</p>
          </div>
          <span className={`px-2 py-0.5 text-xs rounded-full ${device.status === 'ONLINE'
            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
            }`}>
            {device.status}
          </span>
        </div>

        {/* Device Image - Triple click to upload (SUPERADMIN only) */}
        <div
          className={`mb-3 rounded-lg overflow-hidden shadow h-28 md:h-36 relative ${isSuperAdmin ? 'cursor-pointer' : ''}`}
          onClick={() => handlePhotoClick('data-logger', device.id, device.name, device.photo_url)}
          title={isSuperAdmin ? "Kliko 3 herë për të ngarkuar foto" : ""}
        >
          <img
            src={device.photo_url ? `${BACKEND_URL}${device.photo_url}` : defaultPhoto}
            alt="Solar Farm"
            className="w-full h-full object-cover"
          />
          {isSuperAdmin && (
            <div className="absolute bottom-1 right-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">
              3x kliko për foto
            </div>
          )}
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="bg-white dark:bg-[#1e293b] rounded-lg p-2 shadow-sm text-center">
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Grid</p>
            <p className={`text-xs font-semibold ${device.grid_status === 'ON_GRID' ? 'text-green-600' : 'text-gray-600'}`}>
              {device.grid_status === 'ON_GRID' ? 'ON' : 'OFF'}
            </p>
          </div>
          <div className="bg-white dark:bg-[#1e293b] rounded-lg p-2 shadow-sm text-center">
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Power</p>
            <p className="text-xs font-semibold text-gray-900 dark:text-white">{reading?.pv_power || 0} kW</p>
          </div>
          <div className="bg-white dark:bg-[#1e293b] rounded-lg p-2 shadow-sm text-center">
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Today</p>
            <p className="text-xs font-semibold text-gray-900 dark:text-white">{reading?.pv_yield_today || 0}</p>
          </div>
          <div className="bg-white dark:bg-[#1e293b] rounded-lg p-2 shadow-sm text-center">
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Total</p>
            <p className="text-xs font-semibold text-gray-900 dark:text-white">{reading?.pv_yield_total || 0}</p>
          </div>
        </div>

        {/* Add Buttons - Only visible for SUPERADMIN */}
        {isSuperAdmin && (
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => openAddModal('inverter')}
              data-testid="add-inverter-btn"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Shto Inverter
            </button>
            <button
              onClick={() => openAddModal('meter')}
              data-testid="add-meter-btn"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Shto E. Meter
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-[#1e293b] text-gray-600 dark:text-gray-400'
                }`}
            >
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${activeTab === tab.id ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700'
                }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="space-y-2">
          {/* Inverters */}
          {(activeTab === 'all' || activeTab === 'inverters') && inverters.map(inverter => (
            <div
              key={inverter.id}
              className="bg-white dark:bg-[#1e293b] rounded-lg p-3 shadow-sm flex items-center gap-3"
            >
              {/* Inverter Photo - Triple click to upload */}
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${isSuperAdmin ? 'cursor-pointer' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePhotoClick('inverter', inverter.id, inverter.name, inverter.photo_url);
                }}
                title={isSuperAdmin ? "Kliko 3 herë për foto" : ""}
              >
                {inverter.photo_url ? (
                  <img
                    src={`${BACKEND_URL}${inverter.photo_url}`}
                    alt={inverter.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                )}
              </div>

              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => navigate(`/devices/${id}/inverter/${inverter.id}`)}
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{inverter.name}</p>
                  <span className={`px-1.5 py-0.5 text-[10px] rounded ${inverter.status === 'ONLINE'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                    : 'bg-red-100 text-red-700'
                    }`}>
                    {inverter.status}
                  </span>
                </div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">SN: {inverter.serial_number}</p>
                <p className="text-[10px] text-gray-500">{inverter.model || 'SUN2000'} • {inverter.rated_power_kw || 100} kW</p>
              </div>

              {/* Delete button - Only for SUPERADMIN */}
              {isSuperAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete('inverter', inverter.id, inverter.name);
                  }}
                  className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                  title="Fshi"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}

              <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </div>
          ))}

          {/* Energy Meters */}
          {(activeTab === 'all' || activeTab === 'meters') && energyMeters.map(meter => (
            <div
              key={meter.id}
              className="bg-white dark:bg-[#1e293b] rounded-lg p-3 shadow-sm flex items-center gap-3 cursor-pointer"
              onClick={() => {
                const to = buildEnergyMeterDetailPath({ ...meter, data_logger_id: id });
                if (to) navigate(to);
              }}
            >
              {/* Meter Photo - Triple click to upload */}
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${isSuperAdmin ? 'cursor-pointer' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePhotoClick('energy-meter', meter.id, meter.name, meter.photo_url);
                }}
                title={isSuperAdmin ? "Kliko 3 herë për foto" : ""}
              >
                {meter.photo_url ? (
                  <img
                    src={`${BACKEND_URL}${meter.photo_url}`}
                    alt={meter.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm text-gray-900 dark:text-white">{meter.name}</p>
                  <span className={`px-1.5 py-0.5 text-[10px] rounded ${meter.status === 'ONLINE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                    {meter.status}
                  </span>
                </div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">SN: {meter.serial_number}</p>
                <p className="text-[10px] text-gray-500">{meter.model || 'DTSU666'}</p>
              </div>

              {/* Delete button - Only for SUPERADMIN */}
              {isSuperAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete('energy-meter', meter.id, meter.name);
                  }}
                  className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                  title="Fshi"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}

              <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </div>
          ))}

          {/* Accessories - Show all data loggers from same company */}
          {activeTab === 'accessories' && (
            <div className="space-y-2 py-2">
              <p className="text-xs text-gray-500 dark:text-gray-400 px-1 mb-2">Data Loggers të kompanisë</p>
              {/* This will be populated from parent company's data loggers */}
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
                <p className="text-sm">Data Logger: <span className="font-semibold text-gray-700 dark:text-gray-300">{device?.name}</span></p>
                <p className="text-xs text-gray-400 mt-1">SN: {device?.serial_number}</p>
                <p className="text-xs text-gray-400">Kapaciteti: {((device?.installed_capacity_kw || 0) / 1000).toFixed(2)} MW</p>
              </div>
            </div>
          )}

          {/* Empty States */}
          {activeTab === 'inverters' && inverters.length === 0 && (
            <div className="text-center py-6 text-gray-500">
              <p className="text-sm">Nuk ka inverterë</p>
              {isSuperAdmin && (
                <button onClick={() => openAddModal('inverter')} className="mt-2 text-blue-600 text-sm">+ Shto Inverter</button>
              )}
            </div>
          )}
          {activeTab === 'meters' && energyMeters.length === 0 && (
            <div className="text-center py-6 text-gray-500">
              <p className="text-sm">Nuk ka energy meters</p>
              {isSuperAdmin && (
                <button onClick={() => openAddModal('meter')} className="mt-2 text-green-600 text-sm">+ Shto E. Meter</button>
              )}
            </div>
          )}
        </div>

        {/* Add Device Modal - Centered with X button */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl w-full max-w-xs shadow-2xl">
              {/* Header with X button */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  {addType === 'inverter' ? 'Shto Inverter' : 'Shto Energy Meter'}
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Method Selection */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex gap-2">
                  <button
                    onClick={() => setAddMethod('manual')}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-medium transition ${addMethod === 'manual' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Manual
                  </button>
                  <button
                    onClick={() => setAddMethod('scan')}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-medium transition ${addMethod === 'scan' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                    Scan QR
                  </button>
                </div>
              </div>

              {addMethod === 'manual' ? (
                <form onSubmit={handleAddSubmit} className="p-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Serial Number *</label>
                      <input
                        type="text"
                        value={formData.serial_number}
                        onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="SN-INV-006"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Emri *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Inverter 6"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Model</label>
                        <input
                          type="text"
                          value={formData.model}
                          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                          className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="SUN2000"
                        />
                      </div>
                      {addType === 'inverter' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Power (kW)</label>
                          <input
                            type="number"
                            value={formData.rated_power_kw}
                            onChange={(e) => setFormData({ ...formData, rated_power_kw: e.target.value })}
                            className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="100"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2 mt-5">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                      disabled={saving}
                    >
                      Anulo
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition disabled:opacity-50"
                      disabled={saving}
                    >
                      {saving ? 'Duke ruajtur...' : 'Ruaj'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="p-6 text-center">
                  <div className="w-40 h-40 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center">
                    <svg className="w-14 h-14 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Vendos QR kodin brenda kutisë</p>
                  <p className="text-xs text-gray-400 mb-4">Kamera do të hapet automatikisht</p>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="mt-4 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg text-sm"
                  >
                    Mbyll
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photo Upload Modal - mobile: në mes, X */}
        {showPhotoModal && photoTarget && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl w-full max-w-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                  Ngarko Foto për "{photoTarget.name}"
                </h3>
                <button type="button" onClick={() => { setShowPhotoModal(false); setPhotoTarget(null); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" aria-label="Mbyll">✕</button>
              </div>

              <div className="mb-4">
                {photoTarget.currentPhoto ? (
                  <img
                    src={`${BACKEND_URL}${photoTarget.currentPhoto}`}
                    alt="Current"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-full h-40 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <span className="text-gray-400">Nuk ka foto</span>
                  </div>
                )}
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
              />

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowPhotoModal(false);
                    setPhotoTarget(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg text-sm font-medium"
                  disabled={uploading}
                >
                  Anulo
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  disabled={uploading}
                >
                  {uploading ? 'Duke ngarkuar...' : 'Zgjidh Foto'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DeviceDetail;
